import { FaceSmileIcon } from '@/assets/images';
import { Emoji } from './Emoji';
import { useFreezeScroll } from '@/shared/hooks/useFreezeScroll';
import { useOutsideClick } from '@/shared/hooks/useOutsideClick';
import { useSmartPositioning } from '@/shared/hooks/useSmartPositioning';
import PropTypes from 'prop-types';
import { useId, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import './emoji.scss';

let activePickerIdGlobal = null;
const listeners = new Set();

const subscribe = onStoreChange => {
   listeners.add(onStoreChange);
   return () => listeners.delete(onStoreChange);
};

const getSnapshot = () => activePickerIdGlobal;

const setActivePickerIdGlobal = id => {
   activePickerIdGlobal = id;
   listeners.forEach(l => l());
};

const EmojiButton = ({ insertEmoji, containerId, id, activePickerId, setActivePickerId, className = '' }) => {
   const globalActiveId = useSyncExternalStore(subscribe, getSnapshot);

   // Use prop id if provided, otherwise generate a unique ID
   const generatedId = useId();
   const pickerId = id || generatedId;

   // Determine if open based on external props OR the internal singleton state
   const currentActiveId = activePickerId !== undefined ? activePickerId : globalActiveId;
   const updateActivePickerId = setActivePickerId || setActivePickerIdGlobal;

   const isOpen = currentActiveId === pickerId;

   const triggerRef = useRef(null);
   const pickerRef = useRef(null);
   useFreezeScroll(containerId, isOpen);
   const { pickerStyles } = useSmartPositioning({ open: isOpen, triggerRef, containerRef: pickerRef });

   const togglePicker = e => {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) {
         updateActivePickerId(null);
      } else {
         updateActivePickerId(pickerId);
      }
   };

   const closePicker = () => {
      updateActivePickerId(null);
   };

   const handleEmojiClick = emoji => {
      insertEmoji?.(emoji);
   };

   useOutsideClick(pickerRef, e => {
      if (triggerRef.current?.contains(e.target)) {
         return;
      }
      closePicker();
   });

   return (
      <>
         <div className={`emoji-wrapper ct-flexCenter ${className} ${isOpen ? 'is-open' : ''}`}>
            <button
               ref={triggerRef}
               className="emoji-icon"
               aria-label="Emoji picker"
               onMouseDown={togglePicker}
               tabIndex={-1}
               type="button"
            >
               <FaceSmileIcon />
               {/* <img src={} alt="Emoji" className="emoji-trigger-icon" /> */}
            </button>
         </div>

         {isOpen &&
            createPortal(
               <div ref={pickerRef} className="emoji-picker-container" style={pickerStyles}>
                  <Emoji
                     id={pickerId}
                     activeId={currentActiveId}
                     emojiState={isOpen}
                     onEmojiClick={handleEmojiClick}
                     handleCloseEmojiClick={closePicker}
                  />
               </div>,
               document.body,
            )}
      </>
   );
};

EmojiButton.propTypes = {
   insertEmoji: PropTypes.func,
   containerId: PropTypes.string,
   id: PropTypes.string,
   activePickerId: PropTypes.string,
   setActivePickerId: PropTypes.func,
   className: PropTypes.string,
};

export default EmojiButton;
